import { Observable, of, map, catchError } from 'rxjs';
import { ComponentPermissionService, PermissionFunction } from '../component-permission.service';
import { JwtHelperService } from '@auth0/angular-jwt';

export function canEditEvent(service: ComponentPermissionService): PermissionFunction {
  return {
    name: 'Can Edit Event',
    description: 'Check if user can edit a specific event based on role, participation, and location',
    requiresContext: true,
    execute: (context?: any) => {
      
      // If no event ID provided, deny access
      if (!context?.eventId) {
        return of(false);
      }

      if (!service.authService.isAuthenticated) {
        return of(false);
      }

      // Get current user
      const currentUser = service.authService.getCurrentUser();
      if (!currentUser) {
        return of(false);
      }

      // Get user roles and location mappings
      let userRoles: string[] = [];
      let userRoleLocationMap: {[role: string]: string[]} = {};
      
      // Try to get roles from currentUser.roles first
      if (Array.isArray(currentUser.roles)) {
        userRoles = currentUser.roles;
      }
      
      // If roles are not in currentUser, try to get them from authService rolesSubject
      if (userRoles.length === 0) {
        const rolesFromSubject = service.authService.getUserRoles();
        if (rolesFromSubject && rolesFromSubject.length > 0) {
          userRoles = rolesFromSubject;
        }
      }
      
      // Get organizations_id - it might be in the token or user_info
      let organizationsId: number | null = null;
      if (currentUser.organizations_id !== undefined) {
        organizationsId = currentUser.organizations_id;
      } else if (currentUser.user_info?.organizations_id !== undefined) {
        organizationsId = currentUser.user_info.organizations_id;
      } else {
        // Try to get from token
        try {
          const token = service.authService.tokenStateService?.getCurrentToken();
          if (token) {
            const jwtHelper = new JwtHelperService();
            const decoded = jwtHelper.decodeToken(token) as any;
            if (decoded?.organizations_id) {
              organizationsId = decoded.organizations_id;
            }
          }
        } catch (e) {
          console.warn('[canEditEvent] Could not extract organizations_id from token:', e);
        }
      }
      
      // Get location mappings from user_info
      if (currentUser.user_info && typeof currentUser.user_info === 'object') {
        Object.keys(currentUser.user_info).forEach(roleType => {
          if (Array.isArray(currentUser.user_info[roleType]) && currentUser.user_info[roleType].length > 0) {
            userRoleLocationMap[roleType] = currentUser.user_info[roleType];
          }
        });
      }
      

      
      // If we don't have roles yet, wait a bit and try again (user data might still be loading)
      if (userRoles.length === 0) {
        console.warn('[canEditEvent] No user roles found, user data might still be loading. Denying access for now.');
        return of(false);
      }
      
      // If we don't have organizations_id and we need it for orgadmin/orgbruger checks, deny access
      if ((userRoles.includes('orgadmin') || userRoles.includes('orgbruger')) && organizationsId === null) {
        console.warn('[canEditEvent] User has orgadmin/orgbruger role but organizations_id is not available. Denying access.');
        return of(false);
      }

      // 1. SUPERADMIN can always edit any event
      if (userRoles.includes('superadmin')) {
        return of(true);
      }

      // 2. ORGADMIN can edit events only within their own organization
      if (userRoles.includes('orgadmin')) {
        if (organizationsId !== null && organizationsId === context.eventOrganizationId) {
          return of(true);
        } else {
          return of(false);
        }
      }

      // 3. ORGBRUGER can edit events only within their own organization
      if (userRoles.includes('orgbruger')) {
        if (organizationsId !== null && organizationsId === context.eventOrganizationId) {
          return of(true);
        } else {
          return of(false);
        }
      }

      // 4. For other roles (instructors, uddansvarlig, uddassistent), check via backend
      // Get the full event data to check participation and location-based permissions
      return service.http.get(`${service.apiUrl}/events/${context.eventId}`).pipe(
        map((eventData: any) => {
          
          // Check if user is an instructor for this specific event
          if (eventData && eventData.participants && Array.isArray(eventData.participants)) {
            
            const isInstructor = eventData.participants.some((participant: any) => {
              const isRoleMatch = participant.role === 'instructor';
              const isUserMatch = participant.user?.uuid === currentUser.uuid || participant.user?.id === currentUser.id;
              
              
              return isRoleMatch && isUserMatch;
            });
            
            if (isInstructor) {
              return true;
            }
          }
          
          // Location roles only apply inside the same organization as the event
          if (userRoles.includes('uddansvarlig') || userRoles.includes('uddassistent')) {
            const eventOrgId =
              eventData?.organizations_id ??
              eventData?.organization?.id ??
              eventData?.predefined_location?.organizations_id ??
              null;
            if (
              organizationsId === null ||
              eventOrgId === null ||
              Number(eventOrgId) !== Number(organizationsId)
            ) {
              return false;
            }

            const eventLocationCleanName = eventData?.predefined_location?.clean_name
              ? String(eventData.predefined_location.clean_name).toLowerCase()
              : '';

            let hasLocationAccess = false;
            ['uddansvarlig', 'uddassistent'].forEach((roleType) => {
              if (!userRoleLocationMap[roleType]) {
                return;
              }
              const userLocations = userRoleLocationMap[roleType].map((loc: string) =>
                loc.toLowerCase()
              );
              if (
                eventLocationCleanName &&
                userLocations.includes(eventLocationCleanName)
              ) {
                hasLocationAccess = true;
              }
            });

            if (hasLocationAccess) {
              return true;
            }
          }
          
          return false;
        }),
        catchError(error => {
          console.error('[canEditEvent] Error checking event permissions:', error);
          return of(false);
        })
      );
    }
  };
} 